import React, { useCallback, useState } from "react";
import { BaseModal } from "../../atoms/base-modal";
import classnames from "classnames";
import { Group, GroupConst } from "../../../application/domain/group/group";
import { RadioButton, TextField } from "@graffer-inc/console-design-kit";
import { useCheckGroupExistence } from "./hooks/use-check-group-existence";

type GroupNameModalProps = {
  isLoading: boolean;
  organizationId?: string;
  title: string;
  isActive: boolean;
  targetGroup: Group | null;
  onSubmit: (group: Group, organizationUserId?: string) => void;
  onModal: () => void;
  buttonText: string;
  canIgnoreAclSetting: boolean;
};

export const GroupNameModal: React.FC<GroupNameModalProps> = ({
  isLoading,
  title,
  isActive,
  onModal,
  onSubmit,
  targetGroup,
  buttonText,
  canIgnoreAclSetting,
}) => {
  const [groupName, setGroupName] = useState(
    targetGroup ? targetGroup.name : "",
  );
  const [isIgnoreAclSetting, setIsIgnoreAclSetting] = useState(
    targetGroup ? targetGroup.isIgnoreAclSetting : false,
  );

  // グループ名のバリデーション
  // 1. グループ名は1文字以上`GroupConst.MAX_NAME_LENGTH`文字以下であること
  // 2. 既に同名のグループが存在しないこと（なお、同名のグループの作成は許可している）
  const [checkedGroupName, isExistGroup] = useCheckGroupExistence(groupName);
  const getGroupNameError = useCallback(() => {
    // 編集時は同名のグループでメッセージを出さない
    if (targetGroup?.name === checkedGroupName) {
      return "";
    }
    if (
      groupName.length === 0 ||
      groupName.length > GroupConst.MAX_NAME_LENGTH
    ) {
      return `グループ名は1文字以上、${GroupConst.MAX_NAME_LENGTH}文字以下で入力してください`;
    }
    if (isExistGroup) {
      return `${checkedGroupName} は既に存在するグループ名です`;
    }
    return "";
  }, [targetGroup?.name, groupName, checkedGroupName, isExistGroup]);

  return (
    <BaseModal isActive={isActive} title={title} onClose={onModal}>
      <section className="modal-card-body">
        <div className="field">
          <TextField
            label="グループ名"
            description=""
            placeholder="グループ名"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            errorMessages={getGroupNameError()}
            required
          />
        </div>
        {canIgnoreAclSetting && (
          <>
            <div className="field">
              <RadioButton
                label="IPアドレス制御"
                description=""
                onChange={(e) =>
                  setIsIgnoreAclSetting(e.target.value === "inactive")
                }
                options={[
                  { name: "有効にする", value: "active" },
                  { name: "無効にする", value: "inactive" },
                ]}
                value={isIgnoreAclSetting ? "inactive" : "active"}
                direction="row"
              />
            </div>
            {isIgnoreAclSetting && (
              <div className="notification is-warning is-light">
                <div>
                  <span className={`icon has-text-warning`}>
                    <i className="fas fa-exclamation-triangle" />
                  </span>
                  <b>
                    グループの所属ユーザーに対して、IPアドレス制御が無効になります
                  </b>
                </div>
                <div className="mt-2 is-size-7">
                  <p>
                    IPアドレス制御を無効化して問題ないグループかどうかを確認してください。
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      <footer className="modal-card-foot">
        <button
          className="button is-failure is-fullwidth"
          onClick={() => onModal()}
        >
          キャンセル
        </button>
        <button
          className={classnames("button is-info is-fullwidth", {
            "is-loading": isLoading,
          })}
          onClick={() => {
            onSubmit({
              ...targetGroup!,
              name: groupName,
              isIgnoreAclSetting: isIgnoreAclSetting,
            });
            onModal();
          }}
          disabled={groupName === ""}
        >
          {buttonText}
        </button>
      </footer>
    </BaseModal>
  );
};
